import { AutoComplete, FieldMessage } from "components/Form"
import { SmartFormGroup } from "components/SmartContainer/SmartContainer"
import { IProjectOrCategory, ProjectOrCategoryType, UseProjectsAndCategoriesOptions, useProjectsAndCategories } from "../projectHooks"
import { useCallback, useMemo } from "react"
import { useSelector } from "react-redux"
import { RootState } from "base/reducer/reducer"
import { useFormattedProjectOrCategoryValue } from "components/Table/BaseTable/Body/useFormatProjectOrCategory"

type Props = UseProjectsAndCategoriesOptions & {
	value?: ProjectOrCategoryType
	onChange?: (projectOrCategory: ProjectOrCategoryType | undefined) => void
	onBlur?(): void
	disabled?: boolean
	loading?: boolean
	hasError?: boolean // only for show message if select is required, but no value in select
	isRequired?: boolean
	initialProjectOrCategory?: ProjectOrCategoryType
	additionalFilter?: (projectsOrCategories: IProjectOrCategory[]) => IProjectOrCategory[]
	withLabel?: boolean
	size?: 'medium' | 'small'
}

export const ProjectOrCategorySelect = ({
	value,
	onChange,
	onBlur,
	disabled,
	loading,
	hasError,
	isRequired,
	initialProjectOrCategory,
	additionalFilter,
	withLabel = true,
	size,
	...options
}: Props) => {
	const persistedTimeTravelNonProjectCategories = useSelector((state: RootState) => state.persistedTimeTravelNonProjectCategories);
	const formatProjectOrCategory = useFormattedProjectOrCategoryValue();

	const { projectsOrCategories, loadingProjectsAndCategories } = useProjectsAndCategories(options);

	const projectsOrCategoriesWithInitial = useMemo(
		() => {
			let items = [...projectsOrCategories];

			if (additionalFilter) {
				items = additionalFilter(items);
			}

			if (initialProjectOrCategory) {
				const { projectOrCategoryId, isProjectConnected } = initialProjectOrCategory;

				const alreadyIncluded = items.some(item => item.projectOrCategoryId === projectOrCategoryId && item.isProjectConnected === isProjectConnected);

				if (!alreadyIncluded) {
					const label = formatProjectOrCategory(projectOrCategoryId, isProjectConnected);

					items.push({
						id: `${isProjectConnected ? 'project' : 'category'}_${projectOrCategoryId}`,
						label,
						isProjectConnected,
						projectOrCategoryId,
						isReadOnly: isProjectConnected ? false : persistedTimeTravelNonProjectCategories.itemsMap[projectOrCategoryId]?.isReadOnly ?? true
					})
				}
			}

			return items;
		},
		[projectsOrCategories, initialProjectOrCategory, persistedTimeTravelNonProjectCategories.itemsMap, additionalFilter, formatProjectOrCategory]
	)

	const id = useMemo(
		() => {
			if (!value) {
				return;
			}

			const projectOrCategoryOption = projectsOrCategoriesWithInitial.find((item) => item.projectOrCategoryId === value.projectOrCategoryId && item.isProjectConnected === value.isProjectConnected)!;

			return projectOrCategoryOption?.id;
		},
		[projectsOrCategoriesWithInitial, value]
	);

	const onChangeCallback = useCallback(
		(value: string) => {
			const projectOrCategoryOption = projectsOrCategoriesWithInitial.find((item) => item.id === value);

			if (!projectOrCategoryOption) {
				onChange && onChange(undefined);
				return;
			}

			onChange && onChange({
				projectOrCategoryId: projectOrCategoryOption.projectOrCategoryId,
				isProjectConnected: projectOrCategoryOption.isProjectConnected
			})
		},
		[onChange, projectsOrCategoriesWithInitial]
	)

	const autoCompleteControl = useMemo(
		() => (
			<>
				<AutoComplete
					value={id}
					onChange={onChangeCallback}
					onBlur={onBlur}
					items={projectsOrCategoriesWithInitial}
					getItemId={(item: IProjectOrCategory) => item.id}
					getItemText={(item: IProjectOrCategory) => item.label}
					loading={loading || loadingProjectsAndCategories}
					disabled={disabled}
					size={size}
				/>
				{(hasError && isRequired) && <FieldMessage error={{ message: 'i18n.validator.required' }} />}
			</>
		),
		[disabled, hasError, id, isRequired, loading, loadingProjectsAndCategories, onBlur, onChangeCallback, projectsOrCategoriesWithInitial, size]
	)

	return withLabel ? (
		<SmartFormGroup label='i18n.field.projectOrCategory' isRequired={isRequired}>
			{autoCompleteControl}
		</SmartFormGroup>
	) : (
		autoCompleteControl
	)
}
